import React, { useState, useEffect, useCallback, memo } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  Menu,
  X,
  LayoutDashboard,
  Users,
  BookOpen,
  Video,
  FileText,
  ClipboardList,
  ClipboardCheck,
  Bookmark,
  FileArchive,
  UserPlus,
  LogOut,
  Bell,
  Settings,
  GraduationCap,
  Calendar
} from "lucide-react";
import { GiTeacher } from "react-icons/gi";
import { FaWhatsapp, FaBell } from "react-icons/fa";
import PropTypes from "prop-types";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { query, ref, onValue, orderByChild, equalTo, get, update } from "firebase/database";
import logo from "./assets/Xmaths.png";

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [adminName, setAdminName] = useState("Admin");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAdminName("Admin");
        return;
      }

      let resolvedName = user.displayName || "";
      try {
        if (!resolvedName) {
          const usersRef = ref(db, "users");
          const snapshot = await get(usersRef);
          if (snapshot.exists()) {
            const usersData = snapshot.val();
            const foundUser = Object.values(usersData).find((u) => u.email === user.email);
            if (foundUser) {
              resolvedName = foundUser.name || foundUser.fullName || foundUser.username || foundUser.displayName || "";
            }
          }
        }
      } catch (e) {
        // ignore and fallback
      }

      if (!resolvedName && user.email) {
        resolvedName = user.email.split("@")[0];
      }

      setAdminName(resolvedName || "Admin");
    });

    return () => unsubscribe();
  }, []);

  const navigationItems = [
    {
      category: "Main",
      items: [
        { name: "Dashboard", path: "/admin/dashboard", icon: <LayoutDashboard size={18} /> },
        { name: "Courses", path: "/admin/courses", icon: <Settings size={18} /> },
        { name: "Users", path: "/admin/users", icon: <Users size={18} /> },
        // { name: "Students", path: "/admin/students", icon: <Users size={20} /> },
      ]
    },
    {
      category: "Content",
      items: [
        { name: "Chapters", path: "/admin/chapters", icon: <BookOpen size={18} /> },
        { name: "Videos", path: "/admin/videos", icon: <Video size={18} /> },
        { name: "Mark Schemes", path: "/admin/markschemes", icon: <FileText size={18} /> },
        { name: "Extra Questions", path: "/admin/extra-questions", icon: <FileText size={18} /> },
        { name: "PDFs", path: "/admin/pdfs", icon: <FileText size={18} /> },
        { name: "Past Papers", path: "/admin/pastpapers", icon: <FileArchive size={18} /> },
      ]
    },
    {
      category: "Academic",
      items: [
        { name: "Homework Ann.", path: "/admin/homeworks", icon: <ClipboardList size={18} /> },
        { name: "Homework Tracking", path: "/admin/hwtracking", icon: <ClipboardCheck size={18} /> },
        { name: "Exams", path: "/admin/exams", icon: <Bookmark size={18} /> },
        { name: "Attendance", path: "/admin/attendance", icon: <Calendar size={18} /> },
      ]
    },
    {
      category: "Communication",
      items: [
        { name: "Online Meetings", path: "/admin/online-meeting", icon: <Calendar size={18} /> },
        { name: "Registration Requests", path: "/admin/registration-requests", icon: <UserPlus size={18} /> },
        { name: "Announcements", path: "/admin/announcements", icon: <Bell size={18} /> },
      ]
    }
  ];

  return (
    <div className="flex bg-slate-50 text-gray-900 min-h-screen">
      {/* Sidebar Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 sm:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-72 bg-white shadow-xl transition-transform duration-300 z-50 ${sidebarOpen ? "translate-x-0" : "-translate-x-72"} sm:translate-x-0 flex flex-col`}
      >
        {/* Fixed Header */}
        <div className="p-6 border-b border-slate-200 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 shadow-lg flex items-center justify-between">
          <div className="flex items-center justify-center w-full">
            <Link to="/admin/dashboard" className="flex items-center group">
              <img src={logo} alt="XMaths Logo" className="h-16 w-auto" />
            </Link>
          </div>
          {/* Close button for mobile */}
          <button
            className="sm:hidden text-white ml-2"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          >
            <X size={28} />
          </button>
        </div>

        {/* Scrollable Navigation */}
        <div className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-6">
            {navigationItems.map((section) => (
              <div key={section.category}>
                <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2 px-4">
                  {section.category}
                </h3>
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.path}
                        className="flex items-center gap-3 w-full p-2 px-4 rounded-lg text-gray-800 hover:bg-teal-50 hover:text-teal-700 transition duration-200"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="text-gray-600 group-hover:text-teal-700">{item.icon}</span>
                        <span className="text-sm font-medium">{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <Link
            to="/logout"
            className="flex items-center gap-3 w-full p-2 px-4 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition duration-200"
            onClick={() => setSidebarOpen(false)}
          >
            <LogOut size={18} />
            <span className="font-medium">Logout</span>
          </Link>
        </div>
      </div>

      <div className="flex flex-col w-full sm:ml-72">
        {/* Top Navbar */}
        <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 shadow-sm flex justify-between items-center z-40 sm:left-72 min-h-[64px] sm:min-h-[80px]">
          <div className="flex items-center gap-4 px-3 sm:px-4 h-full">
            {/* Hamburger menu for mobile */}
            <button
              className="sm:hidden text-gray-700"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <Menu size={24} />
            </button>
            <div className="sm:hidden">
              <Link to="/admin/dashboard" className="flex items-center group">
                <img src={logo} alt="XMaths Logo" className="h-10 w-auto" />
              </Link>
            </div>
            <div className="hidden sm:block">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Hello {adminName}!</h2>
              <p className="text-xs sm:text-sm text-gray-500">Have a great day</p>
            </div>
          </div>

          <div className="flex items-center gap-4 px-3 sm:px-4 h-full">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                {adminName?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{adminName}</p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="bg-slate-50 min-h-screen pt-20 sm:pt-24 px-3 sm:px-4 md:px-6 pb-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
